/* ==========================================================================
   Logo Flow — stepwise collapsible product flow controller
   Accordion (one open at a time) + per-step "done" detection + auto-advance.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTORS = {
    step: '[data-logo-flow-step]',
    toggle: '[data-logo-flow-toggle]',
    content: '[data-logo-flow-content]',
    subtitle: '[data-logo-flow-subtitle]'
  };

  var state = {
    steps: [],           /* ordered array of step elements */
    doneOnce: new Set(), /* step_ids that have been done at least once (auto-advance fires once) */
    userInteracted: false, /* true after first real user interaction — gates auto-advance */
    priceOverlay: null,  /* { overlay, target, originalDisplay } for top-right price mirroring */
  };

  /* ---------- DOM helpers ---------- */
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function $one(sel, root) { return (root || document).querySelector(sel); }

  function openStep(step) {
    if (!step) return;
    state.steps.forEach(function (s) {
      if (s === step) {
        s.classList.add('is-open');
        var btn = $one(SELECTORS.toggle, s);
        if (btn) btn.setAttribute('aria-expanded', 'true');
      } else {
        s.classList.remove('is-open');
        var btn2 = $one(SELECTORS.toggle, s);
        if (btn2) btn2.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function closeStep(step) {
    step.classList.remove('is-open');
    var btn = $one(SELECTORS.toggle, step);
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  function getStepById(id) {
    for (var i = 0; i < state.steps.length; i++) {
      if (state.steps[i].dataset.logoFlowStep === id) return state.steps[i];
    }
    return null;
  }

  function getNextStep(step) {
    var idx = state.steps.indexOf(step);
    return idx >= 0 && idx < state.steps.length - 1 ? state.steps[idx + 1] : null;
  }

  function setSubtitle(step, text) {
    var el = $one(SELECTORS.subtitle, step);
    if (el) el.textContent = text || '';
  }

  /* ---------- Done detection per step ---------- */

  /**
   * Step 1: COLOR
   * Done when a color option has been chosen. Checks (in order):
   *  - Active siblings swatch (aria-current="true" or .siblings__link--active)
   *  - Checked radio input whose name contains Color/Farve
   *  - Select whose name contains Color/Farve with a non-empty value
   * Returns { done: boolean, label: string|null }
   */
  function checkStepColor() {
    var activeSibling = $one('.siblings [aria-current="true"], .siblings__link--active, .siblings__link[aria-current="true"]');
    if (activeSibling) {
      var sibName = activeSibling.getAttribute('data-color-name')
        || (activeSibling.querySelector('[data-color-name]') && activeSibling.querySelector('[data-color-name]').getAttribute('data-color-name'))
        || (activeSibling.textContent || '').trim();
      return { done: true, label: sibName || null };
    }

    var radios = $all('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var n = (radios[i].name || '').toLowerCase();
      if (n.indexOf('color') !== -1 || n.indexOf('farve') !== -1) {
        return { done: true, label: radios[i].value || null };
      }
    }

    var selects = $all('select');
    for (var j = 0; j < selects.length; j++) {
      var nm = (selects[j].name || '').toLowerCase();
      if ((nm.indexOf('color') !== -1 || nm.indexOf('farve') !== -1) && selects[j].value) {
        return { done: true, label: selects[j].value };
      }
    }

    return { done: false, label: null };
  }

  /**
   * Step 2: LOGO / PLACEMENT (Zepto)
   * Matches the validation in mabala-qty-discount [with Zepto-app]:
   *  - At least one .pplr-checkbox-item is checked (upload-now / send-later)
   *  - At least one .pplr-swatch-element is .selected (placement)
   *  - If "Upload logo nu" is selected, a file must also be attached
   */
  function checkStepLogo() {
    var uploadChoice = $one('input.pplr-checkbox-item:checked');
    var placements = $all('.pplr-swatch-element.selected');

    var uploadNowSelected = false;
    if (uploadChoice) {
      var optionText = (uploadChoice.value || uploadChoice.dataset.label || '') + ' ' +
        ((uploadChoice.parentElement && uploadChoice.parentElement.textContent) || '');
      uploadNowSelected = /upload\s+logo\s+nu/i.test(optionText);
    }

    var hasFile = false;
    if (uploadNowSelected) {
      $all('input[type="file"][name^="properties["]').forEach(function (fi) {
        if (fi.files && fi.files[0]) hasFile = true;
      });
    }

    var done = !!uploadChoice && placements.length > 0 && (!uploadNowSelected || hasFile);

    var label = null;
    if (done) {
      if (placements.length === 1) {
        /* Try to read label text from the selected swatch */
        var txt = (placements[0].textContent || '').trim().replace(/\s+/g, ' ');
        label = txt || '1 placering valgt';
      } else {
        label = placements.length + ' placeringer valgt';
      }
    } else if (uploadNowSelected && !hasFile) {
      label = 'Vedhæft din logo-fil';
    } else if (placements.length > 0 && !uploadChoice) {
      label = 'Vælg logo-upload-metode';
    }
    return { done: done, label: label };
  }

  /**
   * Step 3: QUANTITY
   * Sums all .mqs-size-row__input values from the mabala qty snippet.
   */
  function checkStepQty() {
    var inputs = $all('.mqs-size-row__input');
    var total = 0;
    inputs.forEach(function (inp) {
      var q = parseInt(inp.value, 10);
      if (!isNaN(q) && q > 0) total += q;
    });
    return { done: total > 0, label: total > 0 ? total + ' stk.' : null };
  }

  var STEP_CHECKERS = {
    color: checkStepColor,
    logo: checkStepLogo,
    qty: checkStepQty
  };

  /* ---------- Lock checkers ----------
     Returns { locked: boolean, message: string } — when locked, the step cannot be
     collapsed, and no OTHER step can be opened either. Instead a warning shows. */
  var LOCK_CHECKERS = {
    logo: function () {
      var choice = $one('input.pplr-checkbox-item:checked');
      if (!choice) {
        return {
          locked: true,
          message: 'Vælg venligst "Upload logo nu" eller "Jeg sender mit logo senere" for at fortsætte.'
        };
      }
      var optionText = (choice.value || choice.dataset.label || '') + ' ' +
        ((choice.parentElement && choice.parentElement.textContent) || '');
      if (/upload\s+logo\s+nu/i.test(optionText)) {
        var hasFile = false;
        $all('input[type="file"][name^="properties["]').forEach(function (fi) {
          if (fi.files && fi.files[0]) hasFile = true;
        });
        if (!hasFile) {
          return {
            locked: true,
            message: 'Vedhæft din logo-fil for at fortsætte, eller vælg "Jeg sender mit logo senere".'
          };
        }
      }
      return { locked: false };
    }
  };

  function getStepLock(step) {
    var id = step.dataset.logoFlowStep;
    var fn = LOCK_CHECKERS[id];
    if (typeof fn !== 'function') return { locked: false };
    return fn() || { locked: false };
  }

  /* ---------- Inline warning banner ---------- */
  function ensureWarningEl(step) {
    var content = $one(SELECTORS.content, step);
    if (!content) return null;
    var warn = content.querySelector('.logo-flow__warning');
    if (!warn) {
      warn = document.createElement('div');
      warn.className = 'logo-flow__warning';
      warn.setAttribute('role', 'alert');
      warn.innerHTML = '<span class="logo-flow__warning-icon" aria-hidden="true">!</span><span class="logo-flow__warning-text"></span>';
      content.appendChild(warn);
    }
    return warn;
  }

  var warnTimer = null;
  function showLockWarning(step, message) {
    var warn = ensureWarningEl(step);
    if (!warn) return;
    var txt = warn.querySelector('.logo-flow__warning-text');
    if (txt) txt.textContent = message || '';

    /* Restart animation on repeated triggers */
    warn.classList.remove('is-shown');
    void warn.offsetWidth;
    warn.classList.add('is-shown');

    if (warnTimer) clearTimeout(warnTimer);
    warnTimer = setTimeout(function () {
      warn.classList.remove('is-shown');
      warnTimer = null;
    }, 4000);
  }

  function getOpenLockedStep() {
    for (var i = 0; i < state.steps.length; i++) {
      var s = state.steps[i];
      if (s.classList.contains('is-open') && getStepLock(s).locked) return s;
    }
    return null;
  }

  /* ---------- Quantity steppers (− / + buttons) ----------
     Injects stepper buttons around each .mqs-size-row__input. Idempotent:
     safe to call repeatedly (re-renders from the mabala snippet are caught via
     the general evaluate() loop). */
  function injectSteppers() {
    var inputs = $all('.mqs-size-row__input');
    inputs.forEach(function (inp) {
      if (inp.parentElement && inp.parentElement.classList.contains('logo-flow__qty-stepper')) {
        updateStepperState(inp);
        return;
      }

      var wrap = document.createElement('div');
      wrap.className = 'logo-flow__qty-stepper';

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'logo-flow__qty-btn logo-flow__qty-btn--minus';
      minus.setAttribute('aria-label', 'Minus');
      /* glyph drawn via CSS pseudo-elements for perfect vertical centering */

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'logo-flow__qty-btn logo-flow__qty-btn--plus';
      plus.setAttribute('aria-label', 'Plus');
      /* glyph drawn via CSS pseudo-elements for perfect vertical centering */

      /* Place wrapper where the input was, then move input inside */
      inp.parentNode.insertBefore(wrap, inp);
      wrap.appendChild(minus);
      wrap.appendChild(inp);
      wrap.appendChild(plus);

      minus.addEventListener('click', makeStepperHandler(inp, -1));
      plus.addEventListener('click', makeStepperHandler(inp, 1));

      updateStepperState(inp);
    });
  }

  function makeStepperHandler(inp, delta) {
    return function (e) {
      e.preventDefault();
      e.stopPropagation(); /* don't trigger row's focus-on-click */
      var current = parseInt(inp.value, 10);
      if (isNaN(current)) current = 0;
      var next = Math.max(0, current + delta);
      if (next === current) return;

      inp.value = String(next);
      if (next === 0) {
        inp.classList.add('mqs-size-row__input--empty');
      } else {
        inp.classList.remove('mqs-size-row__input--empty');
      }

      /* Mabala's mqsUpdate is exposed on window — call it to re-render totals */
      if (typeof window.mqsUpdate === 'function') {
        try { window.mqsUpdate(); } catch (_) {}
      }
      /* Also dispatch input event so our own listeners react */
      inp.dispatchEvent(new Event('input', { bubbles: true }));

      updateStepperState(inp);
    };
  }

  function updateStepperState(inp) {
    var wrap = inp.parentElement;
    if (!wrap || !wrap.classList.contains('logo-flow__qty-stepper')) return;
    var minus = wrap.querySelector('.logo-flow__qty-btn--minus');
    var val = parseInt(inp.value, 10);
    if (isNaN(val)) val = 0;
    if (minus) minus.disabled = val <= 0;
  }

  /* ---------- Top-right price mirroring ----------
     Adds a sibling overlay next to the product's .amount element. When the user
     has entered quantities in the mabala snippet, the overlay shows the total and
     the original .amount is visually hidden (display:none — doesn't trigger
     mabala's price MutationObserver which only watches childList/characterData). */
  function setupPriceOverlay() {
    if (state.priceOverlay) return;
    var amountEl = $one('.product-information .price ins .amount, .product-information .product--price .amount');
    if (!amountEl) return;

    /* Hide the whole <ins> if present, else the .amount itself. */
    var target = amountEl.closest('ins') || amountEl;
    var overlay = document.createElement('span');
    overlay.className = 'logo-flow-price-total';
    overlay.style.display = 'none';
    target.parentNode.insertBefore(overlay, target.nextSibling);

    state.priceOverlay = {
      overlay: overlay,
      target: target,
      originalDisplay: target.style.display || ''
    };
  }

  /* ---------- Row price sync ----------
     Mabala's mqsUpdate uses an internal `zeptoAddon` that only updates when
     its MutationObserver sees changes on the .amount node. If Zepto replaces
     .amount (instead of just changing text), the old observer goes stale and
     row prices remain at the base variant price. We read .amount directly
     and override the row ppc text + bar totals so prices stay accurate. */
  function formatMoneyDK(cents) {
    var kr = (cents / 100).toFixed(2);
    var parts = kr.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return whole + ',' + parts[1] + ' kr';
  }

  function syncRowPrices() {
    var inputs = $all('.mqs-size-row__input[data-price]');
    if (inputs.length === 0) return;

    var priceEl = $one('.product-information .price ins .amount, .product-information .product--price .amount');
    if (!priceEl) return;
    var displayed = parseDanishPriceToCents(priceEl.textContent);
    if (displayed === null) return;

    var sampleBase = parseInt(inputs[0].dataset.price, 10);
    if (isNaN(sampleBase) || sampleBase <= 0) return;
    /* Zepto addon is per-unit and color-specific — same across all sizes. */
    var addon = displayed > sampleBase ? displayed - sampleBase : 0;

    /* Current discount % from the badge (mabala keeps this in sync with active tier). */
    var badge = document.getElementById('mqs-discount-badge');
    var disc = 0;
    if (badge) {
      var m = badge.textContent.match(/(\d+(?:\.\d+)?)/);
      if (m) disc = parseFloat(m[1]) || 0;
    }

    /* Compute per-row ppc using each row's own variant base — identical to
       mabala's mqsUpdate. Same numbers from both renderers => no twitch when
       they both run within an input event. */
    var totalQty = 0;
    var totalPrice = 0;
    var rowCalcs = [];
    inputs.forEach(function (inp) {
      var rowBase = parseInt(inp.dataset.price, 10);
      if (isNaN(rowBase)) rowBase = sampleBase;
      var rowEffective = rowBase + addon;
      var rowPpc = Math.round(rowEffective * (100 - disc) / 100);
      var q = parseInt(inp.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      totalQty += q;
      totalPrice += rowPpc * q;
      rowCalcs.push({ inp: inp, ppc: rowPpc, q: q });
    });

    function setText(el, val) {
      if (el && el.textContent !== val) el.textContent = val;
    }

    rowCalcs.forEach(function (rc) {
      var row = rc.inp.closest('.mqs-size-row');
      if (!row) return;
      var ppcStr = formatMoneyDK(rc.ppc) + '/stk.';
      setText(row.querySelector('.mqs-size-row__ppc'), ppcStr);
      if (rc.q > 0) {
        setText(row.querySelector('.mqs-size-row__total'), formatMoneyDK(rc.ppc * rc.q));
      }
    });

    var cartTotalEl = document.getElementById('mqs-cart-total');
    var cartPpcEl = document.getElementById('mqs-cart-ppc');
    var quoteTotalEl = document.getElementById('mqs-quote-total');
    var quotePpcEl = document.getElementById('mqs-quote-ppc');

    if (totalQty === 0) {
      /* Fallback ppc for display when nothing selected — matches sample row. */
      var fallbackPpc = Math.round((sampleBase + addon) * (100 - disc) / 100);
      var fallbackStr = formatMoneyDK(fallbackPpc);
      var fallbackPpcStr = fallbackStr + '/stk.';
      setText(cartTotalEl, fallbackStr);
      setText(cartPpcEl, fallbackPpcStr);
      setText(quoteTotalEl, fallbackStr);
      setText(quotePpcEl, fallbackPpcStr);
    } else {
      var totalStr = formatMoneyDK(totalPrice);
      var avgStr = formatMoneyDK(Math.round(totalPrice / totalQty)) + '/stk.';
      setText(cartTotalEl, totalStr);
      setText(cartPpcEl, avgStr);
      setText(quoteTotalEl, totalStr);
      setText(quotePpcEl, avgStr);
    }
  }

  function updatePriceOverlay() {
    setupPriceOverlay();
    if (!state.priceOverlay) return;

    var inputs = $all('.mqs-size-row__input');
    var totalQty = 0;
    inputs.forEach(function (inp) {
      var q = parseInt(inp.value, 10);
      if (!isNaN(q) && q > 0) totalQty += q;
    });

    var cartTotalEl = document.getElementById('mqs-cart-total');
    var p = state.priceOverlay;

    if (totalQty > 0 && cartTotalEl && cartTotalEl.textContent) {
      p.overlay.textContent = cartTotalEl.textContent;
      p.overlay.style.display = 'inline-block';
      p.target.style.display = 'none';
    } else {
      p.overlay.style.display = 'none';
      p.target.style.display = p.originalDisplay;
    }
  }

  /* ---------- Evaluate all steps + auto-advance ---------- */
  function evaluate() {
    injectSteppers();
    enhanceCartBar();

    state.steps.forEach(function (step) {
      var id = step.dataset.logoFlowStep;
      var checker = STEP_CHECKERS[id];
      if (!checker) return;

      var result = checker();
      var wasDone = step.classList.contains('is-done');

      /* Don't show done state before the user has actually interacted with the page.
         Prevents the initial checkmark on step 1 when a variant is pre-selected by Shopify. */
      if (!state.userInteracted) {
        step.classList.remove('is-done');
        return;
      }

      if (result.done) {
        step.classList.add('is-done');
        if (result.label != null) setSubtitle(step, result.label);

        /* Auto-advance only the FIRST time this step becomes done AND only after user interaction
           (prevents jumping to step 2 on load when a variant is pre-selected). */
        if (!wasDone && !state.doneOnce.has(id)) {
          state.doneOnce.add(id);
          if (state.userInteracted) {
            var next = getNextStep(step);
            if (next && step.classList.contains('is-open')) {
              openStep(next);
            }
          }
        }
      } else {
        step.classList.remove('is-done');
        /* Clear subtitle only if we had set a "done-ish" label */
        if (result.label) setSubtitle(step, result.label);
      }
    });

    syncRowPrices();
    updatePriceOverlay();
  }

  /* ---------- Event wiring ---------- */
  function wireToggles() {
    state.steps.forEach(function (step) {
      var btn = $one(SELECTORS.toggle, step);
      if (!btn) return;
      btn.addEventListener('click', function () {
        /* If ANY currently-open step is locked, allow going BACK to an earlier
           step but block closing the locked one directly or jumping forward. */
        var lockedOpen = getOpenLockedStep();
        if (lockedOpen) {
          var lockedNum = parseInt(lockedOpen.dataset.stepNumber, 10) || 0;
          var thisNum = parseInt(step.dataset.stepNumber, 10) || 0;

          /* Clicking the locked step itself → warn (can't close without resolving) */
          /* Clicking a later step → warn (can't skip forward) */
          if (step === lockedOpen || thisNum > lockedNum) {
            var lockInfo = getStepLock(lockedOpen);
            showLockWarning(lockedOpen, lockInfo.message);
            return;
          }
          /* Clicking an earlier step → allow; fall through to open it */
        }

        if (step.classList.contains('is-open')) {
          closeStep(step);
        } else {
          openStep(step);
        }
      });
    });
  }

  /* ---------- Hard reload on color change ----------
     Zepto binds its placement→addon mapping at page load for the current
     variant. When the user changes color in-page, replaceState updates the URL
     but Zepto does NOT re-initialize, so subsequent placement clicks no longer
     apply the addon for the new variant. The only reliable fix is a real page
     reload — Zepto then runs fresh against the new variant's metadata. We only
     reload if the user has actually progressed past color (placement chosen or
     qty entered); otherwise the URL replaceState is enough. */
  function isColorInput(el) {
    if (!el || !el.name) return false;
    if (el.tagName !== 'INPUT' && el.tagName !== 'SELECT') return false;
    var n = el.name.toLowerCase();
    return n.indexOf('color') !== -1 || n.indexOf('farve') !== -1;
  }

  function userHasProgressedPastColor() {
    var hasQty = false;
    $all('.mqs-size-row__input').forEach(function (inp) {
      var q = parseInt(inp.value, 10);
      if (!isNaN(q) && q > 0) hasQty = true;
    });
    if (hasQty) return true;
    if ($all('.pplr-swatch-element.selected').length > 0) return true;
    if ($one('input.pplr-checkbox-item:checked')) return true;
    return false;
  }

  var reloadScheduled = false;
  function scheduleReloadOnColorChange(e) {
    if (!e || !e.isTrusted) return;
    if (!isColorInput(e.target)) return;
    if (!userHasProgressedPastColor()) return;
    if (reloadScheduled) return;
    reloadScheduled = true;
    /* Let variant-selects updateURL (replaceState) run first so the reload
       lands on the new variant's URL. */
    setTimeout(function () { window.location.reload(); }, 250);
  }

  /* Listen broadly — we re-evaluate on any change inside the product form area */
  function wireListeners() {
    var root = $one('.main-product, main') || document.body;

    ['change', 'input', 'click'].forEach(function (evt) {
      root.addEventListener(evt, function (e) {
        /* Mark as user-interacted only on trusted events (not programmatic). */
        if (e && e.isTrusted) state.userInteracted = true;
        /* Debounce slightly so DOM mutations from other libs settle */
        scheduleEvaluate();
      }, true);
    });

    /* Color-change reload — bound on document so it catches color inputs even
       if they live outside the .main-product root for some themes. */
    document.addEventListener('change', scheduleReloadOnColorChange, true);

    /* MutationObserver catches changes made by Zepto / siblings that don't fire DOM events we see */
    var mo = new MutationObserver(function () { scheduleEvaluate(); });
    mo.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-current', 'checked', 'value']
    });
  }

  var evalTimer = null;
  function scheduleEvaluate() {
    if (evalTimer) return;
    evalTimer = setTimeout(function () {
      evalTimer = null;
      evaluate();
    }, 80);
  }

  /* ---------- Fast add-to-cart ----------
     Intercepts the mabala cart bar click in capture phase and batches the add via
     /cart/add.js with `sections` for inline drawer refresh (no page reload).
     Includes Zepto `_pplr_addprice` + `_pplr_preview` so logo add-on pricing is
     correctly applied. Falls back to the original snippet flow on file upload. */
  var CART_SECTIONS = [
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer--inner' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
  ];

  function hasFileUploadField() {
    var files = $all('input[name^="properties["][type="file"]');
    for (var i = 0; i < files.length; i++) {
      if (files[i].files && files[i].files[0]) return true;
    }
    return false;
  }

  /* Parses "142,38 kr" → 14238 (cents). Mirrors parseDanishPrice in mabala. */
  function parseDanishPriceToCents(text) {
    text = (text || '').trim().replace(/[^\d,\.]/g, '');
    text = text.replace(/\./g, '').replace(',', '.');
    var val = parseFloat(text);
    if (isNaN(val)) return null;
    return Math.round(val * 100);
  }

  /* Re-implements mabala's detectZeptoAddon using DOM values only. */
  function computeZeptoAddonCents() {
    var priceEl = $one('.product-information .price ins .amount, .product-information .product--price .amount');
    if (!priceEl) return 0;
    var displayed = parseDanishPriceToCents(priceEl.textContent);
    if (displayed === null) return 0;

    /* Current variant base price — all .mqs-size-row__input for the current color share the same data-price. */
    var sample = $one('.mqs-size-row__input[data-price]');
    if (!sample) return 0;
    var currentBase = parseInt(sample.dataset.price, 10);
    if (isNaN(currentBase) || currentBase <= 0) return 0;

    return displayed > currentBase ? displayed - currentBase : 0;
  }

  function enhanceCartBar() {
    var cartBar = document.getElementById('mqs-cart-bar');
    if (!cartBar || cartBar.dataset.logoFlowEnhanced === '1') return;
    cartBar.dataset.logoFlowEnhanced = '1';

    cartBar.addEventListener('click', function (ev) {
      /* If a file is selected for upload, let mabala's FormData flow handle it. */
      if (hasFileUploadField()) return;

      ev.stopImmediatePropagation();
      ev.preventDefault();
      runFastAddToCart(cartBar);
    }, true);
  }

  function runFastAddToCart(btn) {
    if (btn.classList.contains('is-loading')) return;

    /* Logo products carry a "logo" step in the flow; on those pages
       personalization is MANDATORY and must be fully validated. On all other
       products Zepto data must never be attached at all. */
    var isLogoProduct = !!document.querySelector('[data-logo-flow-step="logo"]');

    if (isLogoProduct) {
      /* If Zepto's widget hasn't rendered we cannot validate the logo choice.
         Block instead of silently letting the order through without logo
         fee/placement (previously validation was skipped entirely here). */
      if (!$one('input.pplr-checkbox-item')) {
        alert('Personaliseringen er ikke indlæst endnu — vent et øjeblik eller genindlæs siden, og prøv igen.');
        return;
      }
      if (!$one('input.pplr-checkbox-item:checked')) {
        alert('Vælg venligst om du vil uploade logo nu eller sende det senere.');
        return;
      }
      if ($all('.pplr-swatch-element.selected').length === 0) {
        alert('Vælg venligst mindst én broderings- eller trykplacering.');
        return;
      }
    }

    /* Safety check after color change: placement may still look selected
       but Zepto's addon binding for the new variant can drop to 0. Block
       submission so the cart can't go through without the logo fee. */
    var zeptoAddon = isLogoProduct ? computeZeptoAddonCents() : 0;
    var hasPlacement = $all('.pplr-swatch-element.selected').length > 0;
    if (isLogoProduct && hasPlacement && zeptoAddon <= 0) {
      alert('Bekræft din trykplacering igen efter farveskift — klik på den valgte placering én gang til for at sikre korrekt pris med logo-tillæg.');
      return;
    }

    /* Collect shared properties (Zepto hidden inputs etc.).
       Non-logo products must never carry Zepto personalization data — stray
       hidden fields from an idle widget would otherwise make Zepto add
       "Item Personalization" fee lines to the order on import. */
    var sharedProps = {};
    $all('input[name^="properties["]').forEach(function (el) {
      if (el.type === 'file') return;
      var m = el.name.match(/^properties\[(.+)\]$/);
      if (!m || el.value === '') return;
      if (!isLogoProduct && (m[1].indexOf('_pplr') === 0 || el.closest('.product-personalizer, [id^="pplr-"], [class*="pplr"]'))) return;
      sharedProps[m[1]] = el.value;
    });

    /* Add Zepto pricing properties so cart-item reflects logo add-on. Exactly like mabala. */
    if (isLogoProduct && zeptoAddon > 0) {
      sharedProps['_pplr_addprice'] = String(zeptoAddon);
      sharedProps['_pplr_preview'] = 'Preview';
    }

    /* Collect line items */
    var items = [];
    $all('.mqs-size-row__input').forEach(function (inp) {
      var q = parseInt(inp.value, 10);
      if (!isNaN(q) && q > 0 && inp.dataset.variantId) {
        items.push({
          id: parseInt(inp.dataset.variantId, 10),
          quantity: q,
          properties: Object.assign({}, sharedProps)
        });
      }
    });

    if (items.length === 0) {
      alert('Vælg mindst én størrelse med et antal større end 0.');
      return;
    }

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var url = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
    var sections = CART_SECTIONS.map(function (s) { return s.section; })
      .filter(function (v, i, a) { return a.indexOf(v) === i; })
      .join(',');

    /* Kurven før tilføjelsen — bruges til lagertjek og til at se, hvor meget
       Shopify faktisk lagde i kurven, hvis noget bliver afvist. */
    var cartBefore = null;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        cartBefore = cart;
        var shortage = stockShortageLines(items, cart);
        if (shortage.length) {
          alert('Vi har desværre ikke nok på lager:\n\n' + shortage.join('\n') +
            '\n\nRet antallet og prøv igen, eller kontakt os på +45 77 30 05 00, hvis du skal bruge flere.');
          return { skipped: true };
        }
        return addItemsOneByOne(url, items, sections);
      })
      .then(function (result) {
        if (result.skipped) return;
        if (!result.ok) {
          if (result.status === 422 && cartBefore) return handleStockShortage(items, cartBefore, result.data);
          throw result.data || { description: 'Cart add failed' };
        }
        if (result.data && result.data.sections) {
          applyCartSections(result.data.sections);
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        openCartDrawer();
      })
      .catch(function (err) {
        console.error('Cart error:', err);
        alert(shopifyErrorText(err) || 'Noget gik galt. Prøv igen.');
      })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  }

  /* Shopify's batch-format ({ items: [...] }) lægger højst 32 stk. pr. variant i
     kurven, så hver størrelse tilføjes for sig. Sidste kald henter sektionerne
     til kurv-skuffen. Stopper ved første fejl. */
  function addItemsOneByOne(url, items, sections) {
    var i = 0;
    function next() {
      var it = items[i];
      var last = i === items.length - 1;
      var body = { id: it.id, quantity: it.quantity, properties: it.properties };
      if (last) { body.sections = sections; body.sections_url = window.location.pathname; }
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, status: res.status, data: data }; });
        })
        .then(function (result) {
          if (!result.ok || last) return result;
          i++;
          return next();
        });
    }
    return next();
  }

  /* Tjekker antal mod det reelle lagertal (h5-variant-stock.liquid) for varianter,
     der er sat til "stop salg ved udsolgt". Tager højde for det, der allerede
     ligger i kurven. */
  function stockShortageLines(items, cart) {
    var stock = window.h5VariantStock || {};
    var lines = [];
    items.forEach(function (it) {
      var s = stock[it.id];
      if (!s || !s.deny) return;
      var inCart = cart ? qtyInCart(cart, it.id) : 0;
      var left = Math.max(0, s.qty - inCart);
      if (it.quantity <= left) return;
      var label = sizeLabelFor(it.id) || 'Størrelse';
      var line = '• ' + label + ': kun ' + s.qty + ' stk. på lager (du valgte ' + it.quantity + ')';
      if (inCart > 0) line += ' — du har allerede ' + inCart + ' i kurven, så du kan tilføje ' + left + ' mere';
      lines.push(line + '.');
    });
    return lines;
  }

  /* Shopify's /cart/add svarer med { errors }, /cart/add.js med { description }. */
  function shopifyErrorText(err) {
    if (!err) return '';
    if (typeof err.description === 'string') return err.description;
    if (typeof err.errors === 'string') return err.errors;
    if (typeof err.message === 'string') return err.message;
    return '';
  }

  function sizeLabelFor(variantId) {
    var inp = $one('.mqs-size-row__input[data-variant-id="' + variantId + '"]');
    var row = inp && inp.closest('.mqs-size-row');
    var label = row && row.querySelector('.mqs-size-row__label');
    return label ? label.textContent.trim() : '';
  }

  function qtyInCart(cart, variantId) {
    var q = 0;
    (cart.items || []).forEach(function (it) { if (it.variant_id === variantId) q += it.quantity; });
    return q;
  }

  /* Shopify afviser (422) når en variant er sat til "stop salg ved udsolgt" og
     der ikke er nok på lager. Den lægger dog det, der er, i kurven — så vi
     sammenligner kurven før/efter og fortæller kunden præcis, hvad der mangler. */
  function handleStockShortage(items, cartBefore, errData) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cartAfter) {
        var lines = [];
        var anyAdded = false;
        items.forEach(function (it) {
          var added = qtyInCart(cartAfter, it.id) - qtyInCart(cartBefore, it.id);
          if (added > 0) anyAdded = true;
          if (added >= it.quantity) return;
          var label = sizeLabelFor(it.id) || 'Størrelse';
          lines.push(added > 0
            ? '• ' + label + ': kun ' + added + ' stk. på lager (du valgte ' + it.quantity + '). ' + added + ' stk. er lagt i kurven.'
            : '• ' + label + ': ikke nok på lager (du valgte ' + it.quantity + '). Ikke lagt i kurven.');
        });

        if (!lines.length) throw errData || { description: 'Cart add failed' };

        alert('Vi har desværre ikke nok på lager til hele din bestilling:\n\n' + lines.join('\n') +
          '\n\nRet antallet og prøv igen, eller kontakt os på +45 77 30 05 00, hvis du skal bruge flere.');

        if (anyAdded) {
          return fetch('/?sections=cart-drawer,cart-bubble')
            .then(function (res) { return res.json(); })
            .then(function (sectionsMap) {
              applyCartSections(sectionsMap);
              openCartDrawer();
            })
            .catch(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); });
        }
      });
  }

  function applyCartSections(sectionsMap) {
    CART_SECTIONS.forEach(function (m) {
      var html = sectionsMap[m.section];
      if (!html) return;
      var container = document.getElementById(m.id);
      if (!container) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var src = parsed.querySelector(m.selector);
      var dst = container.querySelector(m.selector) || container;
      if (src && dst) dst.innerHTML = src.innerHTML;
    });

    /* Re-wire theme's remove/qty listeners — they're lost when innerHTML is swapped.
       Without this, clicking the trash icon falls back to <a href="/cart"> navigation. */
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (typeof drawer.setupEventListeners === 'function') {
        try { drawer.setupEventListeners(); } catch (_) {}
      } else if (typeof drawer.removeProductEvent === 'function') {
        try { drawer.removeProductEvent(); } catch (_) {}
      }
    }

    try { document.dispatchEvent(new CustomEvent('cart:item-added')); } catch (_) {}
  }

  function openCartDrawer() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return;
    drawer.removeAttribute('inert');
    drawer.classList.remove('cart-drawer--empty');
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      try { drawer.dispatchEvent(new CustomEvent('cart-drawer:open')); } catch (_) {}
    }
  }

  /* ---------- Init ----------
     Runs on every product page. If logo-flow steps are present: accordion + lock + done
     + auto-advance activate. If not (e.g. "uden logo" templates): only stepper
     injection, input-box styling, and the top-price overlay run. */
  function init() {
    var steps = $all(SELECTORS.step);

    if (steps.length > 0) {
      steps.sort(function (a, b) {
        return (parseInt(a.dataset.stepNumber, 10) || 0) - (parseInt(b.dataset.stepNumber, 10) || 0);
      });
      state.steps = steps;

      /* Start all closed, then open first */
      steps.forEach(function (s) { s.classList.remove('is-open'); });
      openStep(steps[0]);

      wireToggles();
    }

    wireListeners();

    /* Initial evaluation + staged re-eval (Zepto/siblings may hydrate after load).
       evaluate() is a no-op for features whose DOM isn't present. */
    evaluate();
    setTimeout(evaluate, 500);
    setTimeout(evaluate, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
